import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import Grow from '@material-ui/core/Grow';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { makeStyles, fade, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import API_Service, { axiosDefaultConfig, axiosPutConfig, axiosURIencode } from '../../Services/API_Service';
import { empty, req_promotion_type } from '../../Helper/Constants';

const GrowTransition = React.forwardRef(function GrowTransition(props, ref) {
	return <Grow ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
	paper: {
		borderRadius: 16,
		width: 480,
		maxWidth: '100%'
	},
	title: {
		paddingBottom: theme.spacing(0.5),
		'& h2': {
			fontWeight: 700
		}
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5)
	},
	field: {
		'& .MuiOutlinedInput-root': {
			borderRadius: 10,
			transition: theme.transitions.create('box-shadow', { duration: 180 })
		},
		'& .MuiOutlinedInput-root.Mui-focused': {
			boxShadow: `0 0 0 3px ${fade('#FEF200', 0.35)}`
		}
	},
	statusRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 10,
		padding: theme.spacing(1, 1, 1, 1.75)
	},
	statusLeft: {
		display: 'flex',
		flexDirection: 'column'
	},
	statusLabel: {
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
		color: theme.palette.text.secondary
	},
	statusValue: {
		fontWeight: 600,
		transition: theme.transitions.create('color', { duration: 150 })
	},
	statusOn: {
		color: theme.palette.type === 'dark' ? '#7ee2a8' : '#1b7a43'
	},
	statusOff: {
		color: theme.palette.text.disabled
	},
	actions: {
		padding: theme.spacing(1, 3, 2)
	},
	saveBtn: {
		backgroundColor: '#FEF200',
		color: 'rgba(0,0,0,0.86)',
		fontWeight: 700,
		borderRadius: 10,
		minWidth: 108,
		paddingLeft: theme.spacing(2.5),
		paddingRight: theme.spacing(2.5),
		transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], { duration: 180 }),
		'&:hover': {
			backgroundColor: '#FFF53D',
			transform: 'translateY(-1px)',
			boxShadow: `0 6px 16px ${fade('#FEF200', 0.5)}`
		},
		'&:active': {
			transform: 'translateY(0)'
		},
		'&.Mui-disabled': {
			backgroundColor: fade('#FEF200', 0.4),
			color: 'rgba(0,0,0,0.4)'
		}
	},
	spinner: {
		color: 'rgba(0,0,0,0.6)'
	}
}));

export default function PromotionTypeDialog({ open, mode, data, client, language, onSnack, onClose }) {
	const classes = useStyles();
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

	const [name, setName] = useState(empty);
	const [description, setDescription] = useState(empty);
	const [status, setStatus] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			if (mode === 'edit' && data) {
				setName(data.name || empty);
				setDescription(data.description || empty);
				setStatus(parseInt(data.status) === 1);
			} else {
				setName(empty);
				setDescription(empty);
				setStatus(true);
			}
		}
	}, [open, mode, data]);

	const handleClose = () => {
		if (saving) {
			return;
		}
		onClose(false);
	};

	const handleResult = (res, okMsg, failMsg) => {
		setSaving(false);
		if (parseInt(res.result) === 1) {
			onSnack(okMsg);
			onClose(true);
		} else {
			onSnack(res.message || failMsg, 'error');
		}
	};

	const submit = () => {
		if (name.trim() === empty) {
			onSnack(language.enter_promotion_type_name || 'Please enter Type Name', 'warning');
			return;
		}
		setSaving(true);
		if (mode === 'edit' && data) {
			const params = axiosURIencode({
				id: data.id,
				name: name.trim(),
				description: description,
				status: status ? 1 : 0
			});
			API_Service.put(req_promotion_type, params, axiosPutConfig(client))
				.then(({ data: res }) => handleResult(res, language.update_success, language.update_fail))
				.catch(() => { setSaving(false); onSnack(language.no_internet || 'Network error', 'error'); });
		} else {
			const formData = new FormData();
			formData.append('name', name.trim());
			formData.append('description', description);
			formData.append('status', status ? 1 : 0);
			API_Service.post(req_promotion_type, formData, axiosDefaultConfig(client))
				.then(({ data: res }) => handleResult(res, language.insert_success, language.insert_fail))
				.catch(() => { setSaving(false); onSnack(language.no_internet || 'Network error', 'error'); });
		}
	};

	return (
		<Dialog
			fullScreen={fullScreen}
			open={open}
			onClose={handleClose}
			TransitionComponent={GrowTransition}
			PaperProps={{ className: classes.paper }}
			aria-labelledby="promotion-type-dialog-title">
			<DialogTitle id="promotion-type-dialog-title" className={classes.title}>
				{mode === 'edit' ? language.edit_promotion_type : language.new_promotion_type}
			</DialogTitle>
			<DialogContent>
				<div className={classes.content}>
					<TextField
						className={classes.field}
						variant="outlined"
						label={language.promotion_type_name}
						value={name}
						onChange={(e) => setName(e.target.value)}
						required
						autoFocus
						fullWidth
						inputProps={{ maxLength: 400 }}
					/>
					<TextField
						className={classes.field}
						variant="outlined"
						label={language.description}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						multiline
						rows={3}
						fullWidth
						inputProps={{ maxLength: 1000 }}
					/>
					<div className={classes.statusRow}>
						<div className={classes.statusLeft}>
							<span className={classes.statusLabel}>{language.status}</span>
							<Typography
								variant="body2"
								className={clsx(classes.statusValue, status ? classes.statusOn : classes.statusOff)}>
								{status ? (language.active || 'Active') : (language.inactive || 'Inactive')}
							</Typography>
						</div>
						<Switch checked={status} onChange={(e) => setStatus(e.target.checked)} color="primary" />
					</div>
				</div>
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button onClick={handleClose} color="inherit" disabled={saving}>
					{language.cancel}
				</Button>
				<Button
					onClick={submit}
					className={classes.saveBtn}
					variant="contained"
					disableElevation
					disabled={saving || name.trim() === empty}
					startIcon={saving ? <CircularProgress size={16} className={classes.spinner} /> : null}>
					{language.save}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
